/**
 * Everything a business has brought in, as a list.
 *
 * A table, like Documents, rather than a grid of cards. Cards were fine for four sets
 * of data and poor for forty: they could not be scanned by size or by date, and the
 * whole card was a link, so there was nowhere to put an action and no way to delete a
 * set of data from its own list. The column names stay under each name, because
 * "Client · Portfolio Value · As At" is what makes a list somebody can pick from
 * without opening anything.
 *
 * The folders are the sidebar's, a level at a time, so a set of data filed away in the
 * tree is filed away here too.
 */
import { useEffect, useMemo, useState } from "react";
import { api, type DatasetSummary, type FolderRow, type TemplateSummary } from "../api.js";
import { reportFailure } from "../App.js";
import { changed, useChanges } from "../changes.js";
import { PROVIDERS } from "../connectors/index.js";
import { FolderCrumbs, FolderTableRow } from "../shell/Folders.js";
import { folderView } from "../shell/tree.js";
import { Button, Confirm, Empty, Icon, Note, Skeleton, ago, bytes, count, lowerDay, toast, when } from "../ui/index.js";
import { atLimit, upgradeTarget, useWorkspace } from "../workspace.js";

export function DataList({ folder }: { folder: string | null }) {
  const { view, refresh } = useWorkspace();
  const changes = useChanges();
  const [datasets, setDatasets] = useState<DatasetSummary[] | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DatasetSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.datasets(), api.templates(), api.folders()])
      .then(([d, t, f]) => {
        if (cancelled) return;
        setDatasets(d.datasets);
        setTemplates(t.templates);
        setFolders(f.folders);
      })
      .catch((e) => !cancelled && setFailed(e instanceof Error ? e.message : "We couldn't load this."));
    return () => {
      cancelled = true;
    };
  }, [changes]);

  const place = useMemo(
    () =>
      folderView(
        folders.filter((f) => f.section === "data"),
        (datasets ?? []).map((d) => ({ id: d.id, name: d.name, folderId: d.folderId, updatedAt: d.updatedAt })),
        folder
      ),
    [folders, datasets, folder]
  );

  /** The designs using each set of data, so deleting one says what it would affect. */
  const usedBy = useMemo(() => {
    const byDataset = new Map<string, string[]>();
    for (const t of templates) if (t.dataset) byDataset.set(t.dataset.id, [...(byDataset.get(t.dataset.id) ?? []), t.name]);
    return byDataset;
  }, [templates]);

  const full = atLimit(view, "datasets");
  const upgrade = upgradeTarget(view);

  if (failed) {
    return (
      <Empty title="We couldn't load your data" actions={<Button variant="primary" onClick={() => window.location.reload()}>Try again</Button>}>
        {failed}
      </Empty>
    );
  }

  if (!datasets) {
    return (
      <div className="wrap stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 36, width: 220 }} />
        <Skeleton rows={3} height={64} />
      </div>
    );
  }

  if (!datasets.length && !folders.some((f) => f.section === "data")) {
    return (
      <div className="wrap">
        <Empty
          title="Bring in your first spreadsheet"
          actions={
            <>
              <a className="btn btn--primary btn--lg" href="/data/new">
                <Icon name="upload" /> Bring in data
              </a>
              <a className="btn btn--lg" href="/data/new/table">
                <Icon name="pencil" /> Type it in
              </a>
            </>
          }
        >
          Drag in an Excel file or a CSV, or paste rows straight out of a spreadsheet. BroadPaper works out the columns, the types and the formats, and
          shows you what it found. No spreadsheet? Type the rows in instead.
        </Empty>
      </div>
    );
  }

  const shown = datasets.filter((d) => place.itemIds.has(d.id));

  return (
    <div className="wrap stack" style={{ gap: 20 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">Your data</span>
          <h1>
            {count(datasets.length)} set{datasets.length === 1 ? "" : "s"} of data
          </h1>
          <p className="head__lede">
            {count(view.usage.datasets)} of {count(view.limits.datasets)} on your plan · {bytes(view.usage.storageBytes)} of {bytes(view.limits.storageBytes)}{" "}
            stored
          </p>
        </div>
        <div className="head__actions">
          <a className="btn" href="/data/new/table" aria-disabled={full}>
            <Icon name="pencil" /> Type it in
          </a>
          <a className="btn btn--primary" href="/data/new" aria-disabled={full}>
            <Icon name="plus" /> Bring in data
          </a>
        </div>
      </div>

      {full ? (
        <Note tone="ask" title={`You are using all ${view.limits.datasets} sets of data your plan includes`}>
          Delete one you no longer need to bring in another{upgrade ? `, or move to ${upgrade.name} for ${count(upgrade.limits.datasets)}.` : "."}
        </Note>
      ) : null}

      <FolderCrumbs base="/data" label="Your data" crumbs={place.crumbs} />

      <div className="table__scroll" style={{ maxHeight: "none" }}>
        <table className="table">
          <thead>
            <tr>
              <th>Data</th>
              <th className="num" style={{ width: 90 }}>
                Rows
              </th>
              <th className="num" style={{ width: 90 }}>
                Size
              </th>
              <th style={{ width: 170 }}>Used by</th>
              <th style={{ width: 130 }}>Changed</th>
              <th style={{ width: 280 }} />
            </tr>
          </thead>
          <tbody>
            {place.folders.map((f) => (
              <FolderTableRow key={f.id} base="/data" folder={f} span={6} noun="item" />
            ))}
            {shown.map((dataset) => {
              const designs = usedBy.get(dataset.id) ?? [];
              return (
                <tr key={dataset.id}>
                  <td>
                    <div className="stack" style={{ gap: 3 }}>
                      <a className="table__link" href={`/data/${dataset.id}`}>
                        <strong>{dataset.name}</strong>
                      </a>
                      {/* The columns themselves, which is what makes this a list somebody
                          can choose from without opening anything. */}
                      <span className="small muted" style={{ lineHeight: 1.45 }}>
                        {dataset.columns.slice(0, 5).join(" · ")}
                        {dataset.columns.length > 5 ? ` · and ${dataset.columns.length - 5} more` : ""}
                      </span>
                      {/* Where next month's rows come from, which is the thing somebody
                          looking for "the one from Drive" is scanning for. */}
                      {dataset.link ? (
                        <span className="small muted row" style={{ gap: 4 }}>
                          <Icon name="cloud" /> {PROVIDERS[dataset.link.provider].name} · refreshed {lowerDay(ago(dataset.refreshedAt))}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="num">{count(dataset.rowCount)}</td>
                  <td className="num small muted">{bytes(dataset.bytes)}</td>
                  <td className="small muted">{designs.length === 0 ? "—" : designs.length === 1 ? designs[0] : `${count(designs.length)} designs`}</td>
                  <td className="small muted">{when(dataset.updatedAt)}</td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                      <a className="btn btn--sm" href={`/data/${dataset.id}/edit`} title={`Change the rows in “${dataset.name}”`}>
                        <Icon name="pencil" /> Edit
                      </a>
                      <a className="btn btn--sm" href={`/designs?data=${dataset.id}`}>
                        Make a document
                      </a>
                      <Button size="sm" variant="quiet" icon="trash" title={`Delete “${dataset.name}”`} onClick={() => setDeleting(dataset)} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!place.folders.length && !shown.length ? <p className="muted">Nothing in this folder yet. Move a set of data here from the sidebar.</p> : null}

      {deleting ? (
        <Confirm
          title={`Delete “${deleting.name}”?`}
          confirmLabel="Delete it"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            const target = deleting;
            setDeleting(null);
            try {
              const result = await api.deleteDataset(target.id);
              setDatasets((current) => (current ?? []).filter((d) => d.id !== target.id));
              await refresh();
              changed();
              toast(
                result.designsAffected.length
                  ? `Deleted. ${result.designsAffected.length === 1 ? `“${result.designsAffected[0]}” needs` : `${count(result.designsAffected.length)} designs need`} data again.`
                  : "Deleted.",
                result.designsAffected.length ? "info" : "good"
              );
            } catch (e) {
              reportFailure(e, "We couldn't delete that.");
            }
          }}
        >
          The rows go for good.{" "}
          {(() => {
            const designs = usedBy.get(deleting.id) ?? [];
            if (!designs.length) return "No design uses it.";
            return `${designs.length === 1 ? `“${designs[0]}” uses it` : `${count(designs.length)} designs use it`} and will ask for data again — no design is deleted.`;
          })()}{" "}
          Documents already made are not affected.
        </Confirm>
      ) : null}
    </div>
  );
}
